import { apiClient } from '@/plugins/axios/api'
import type {
  FondLayerStyle,
  MapStylesListEntry,
  MapStylesListResponse,
} from '@/models/interfaces/map/FondLayerStyle'

const OSM_SUBCATEGORY_PREFIX = 'osm_subcategory_'

/**
 * Returns true if the list entry is an OSM subcategory style (id starts with osm_subcategory_).
 */
export function isOsmEntry(entry: { id: string }): boolean {
  return entry.id.startsWith(OSM_SUBCATEGORY_PREFIX)
}

/**
 * Returns true if the fond/map-style entry belongs to the given atlas category.
 * Checks both `atlas_category` and `category_name` metadata fields,
 * so entries with subcategory_id === null (e.g. Cadastre SOA) are matched too.
 */
export function fondMatchesCategory(
  entry: FondLayerStyle | MapStylesListEntry,
  categoryName: string,
): boolean {
  const meta = entry.metadata as unknown as Record<string, unknown> | undefined
  if (!meta) return false
  return meta.atlas_category === categoryName || meta.category_name === categoryName
}

export interface MapStylesListSplit {
  fondAndIndicators: MapStylesListEntry[]
  osmEntries: MapStylesListEntry[]
  osmByCategory: Record<
    string,
    { atlasGroup?: string; items: MapStylesListEntry[] }
  >
}

/**
 * Fetches the list from GET /api/map_styles and splits into fond/indicators vs OSM,
 * and groups OSM entries by category_name for display.
 */
export async function getMapStylesListSplit(): Promise<MapStylesListSplit> {
  const all = await getMapStylesList()
  const fondAndIndicators = all.filter((s) => !isOsmEntry(s))
  const osmEntries = all.filter(isOsmEntry)
  const osmByCategory = osmEntries.reduce(
    (acc, entry) => {
      const categoryName =
        (entry.metadata?.category_name as string) ?? 'other'
      if (!acc[categoryName]) {
        acc[categoryName] = {
          atlasGroup: entry.metadata?.category_atlas_group as string | undefined,
          items: [],
        }
      }
      acc[categoryName].items.push(entry)
      return acc
    },
    {} as MapStylesListSplit['osmByCategory'],
  )
  return { fondAndIndicators, osmEntries, osmByCategory }
}

/**
 * Fetches the list of map styles from GET /api/map_styles.
 * Each entry includes id and metadata (label, description, visible_in; OSM entries also have category_*).
 */
export async function getMapStylesList(): Promise<MapStylesListEntry[]> {
  try {
    const response = await apiClient.get<MapStylesListResponse>('/api/map_styles')
    const data = response.data
    if (!data?.styles || !Array.isArray(data.styles)) {
      return []
    }
    return data.styles
  } catch {
    return []
  }
}
